import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Download } from "lucide-react";

/**
 * InstallPrompt
 * - Captures the `beforeinstallprompt` event and shows a small banner
 *   inviting the user to install Azure Gypsy Farms to their home screen.
 * - Detects iOS and shows iOS-specific instructions (since iOS doesn't fire the event).
 * - Remembers dismissal in localStorage for 14 days.
 */
const DISMISS_KEY = "agf_pwa_dismissed_at";
const DISMISS_DAYS = 14;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !window.MSStream;
}

function wasRecentlyDismissed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const then = parseInt(raw, 10);
  if (!Number.isFinite(then)) return false;
  return Date.now() - then < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS path
    if (isIos()) {
      const t = setTimeout(() => setShowIos(true), 4000);
      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setShowIos(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted" || outcome === "dismissed") {
      setShow(false);
      setDeferred(null);
    }
  };

  if (show) {
    return (
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-md rounded-2xl border border-emerald-200 bg-stone-50/95 p-4 shadow-2xl backdrop-blur md:left-auto md:right-6 md:bottom-6"
      >
        <div className="flex items-start gap-3">
          <img src="/icon-192.png" alt="" className="h-12 w-12 rounded-xl" />
          <div className="flex-1">
            <p className="font-serif text-base text-emerald-900">Install Azure Gypsy Farms</p>
            <p className="mt-1 text-xs text-stone-600">
              Add to your home screen for one-tap access to the marketplace.
            </p>
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                onClick={install}
                data-testid="pwa-install-accept"
                className="bg-emerald-800 text-stone-50 hover:bg-emerald-900"
              >
                <Download className="mr-1.5 h-4 w-4" />
                Install
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                className="text-stone-600 hover:bg-stone-200"
              >
                Not now
              </Button>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={dismiss}
            className="text-stone-400 hover:text-stone-700"
            data-testid="pwa-install-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (showIos) {
    return (
      <div
        data-testid="pwa-install-banner-ios"
        className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-md rounded-2xl border border-emerald-200 bg-stone-50/95 p-4 shadow-2xl backdrop-blur md:left-auto md:right-6 md:bottom-6"
      >
        <div className="flex items-start gap-3">
          <img src="/icon-192.png" alt="" className="h-12 w-12 rounded-xl" />
          <div className="flex-1">
            <p className="font-serif text-base text-emerald-900">Install on iPhone</p>
            <p className="mt-1 text-xs text-stone-600">
              Tap <span className="font-semibold">Share</span> in Safari, then{" "}
              <span className="font-semibold">Add to Home Screen</span>.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={dismiss}
            className="text-stone-400 hover:text-stone-700"
            data-testid="pwa-install-close-ios"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPrompt;
